//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { createSelector } from 'reselect';

import {
  Like2BuyV2ModerationItemFilter,
  Like2BuyV2ModerationStatus,
} from '@apis/like2buy-v2/like2BuyV2ModerationModel';
import { MAXIMUM_PINNED_ITEMS_COUNT } from '@like2buy-v2/Like2BuyV2UIConstants';
import { ICuralateStore } from '@models/AppState';
import Cuid from '@models/Cuid';
import { createHasFeatureSelector } from '@redux/selectors/createHasFeatureSelector';
import { getClientId } from '@redux/selectors/getClientId';
import { DEFAULT_TIKTOK_LIKE2BUY_FILTER } from '@tiktok/components/like2buy/TikTokLike2BuyUIConstants';
import { hasTikTokLike2BuyModerationPermission } from '@tiktok/redux/like2buy/tiktokLike2BuyModel';
import { getFetchTikTokUserError } from '@tiktok/redux/user/selectors/getTikTokUser';
import { isDefined } from '@utils/filterPredicates';
import { Normalized } from '@utils/normalize';

export const getIsLike2BuyTiktokEnabled = createHasFeatureSelector(
  FeatureNames.Like2BuyTiktok,
);

export const getTikTokLike2Buy = (state: ICuralateStore) =>
  state.tiktok.like2buy;

export const getTikTokLike2BuyStore = createSelector(
  getTikTokLike2Buy,
  (state) => state.store,
);

export const getTikTokLike2BuyStoreConfig = createSelector(
  getTikTokLike2BuyStore,
  (store) => store?.config,
);

export const getTikTokLike2BuyModals = createSelector(
  getTikTokLike2Buy,
  (state) => state.modals,
);

export const getTikTokLike2BuyFlags = createSelector(
  getTikTokLike2Buy,
  (state) => state.flags,
);

export const getTikTokLike2BuyErrors = createSelector(
  getTikTokLike2Buy,
  (state) => state.errors,
);

export const getTikTokLike2BuyOrderedItems = createSelector(
  getTikTokLike2Buy,
  (state) => Normalized.getOrdered(state.items),
);

const getTotalCount = createSelector(
  getTikTokLike2Buy,
  (state) => state.totalCount,
);

export const getTikTokLike2BuyPinnedItems = createSelector(
  getTikTokLike2Buy,
  (state) => state.pinnedItems,
);

export const getTikTokLike2BuyPinnedItemCount = createSelector(
  getTikTokLike2BuyPinnedItems,
  (pinnedItems) => pinnedItems.length,
);

export const getTikTokLike2BuyPinnedItemCuids = createSelector(
  getTikTokLike2BuyPinnedItems,
  (pinnedItems) => pinnedItems.map((item) => item.cuid),
);

export const getTikTokLike2BuyCurrentItemFilter = createSelector(
  getTikTokLike2Buy,
  (tiktokLike2Buy) => tiktokLike2Buy.currentItemFilter,
);

export const getTikTokLike2BuyHasFetchedAllItems = createSelector(
  getTikTokLike2BuyFlags,
  (tiktokLike2BuyFlags) => tiktokLike2BuyFlags.hasFetchedAllItems,
);

export const getTikTokLike2BuyStoreData = createSelector(
  getTikTokLike2Buy,
  (tiktokLike2Buy) => tiktokLike2Buy.store,
);

export const getTikTokLike2BuyPreviewUrl = createSelector(
  getTikTokLike2Buy,
  (tiktokLike2Buy) => tiktokLike2Buy.previewUrl,
);

export const getTikTokLike2BuyStoreId = createSelector(
  getTikTokLike2BuyStoreData,
  (store) => store?.id,
);

export const getTikTokLike2BuyStoreUrl = createSelector(
  getTikTokLike2BuyStoreData,
  (store) => store?.shortlink,
);

export const getTikTokLike2BuyShouldShowEmptyState = createSelector(
  getTikTokLike2BuyFlags,
  getTikTokLike2BuyCurrentItemFilter,
  getTikTokLike2BuyOrderedItems,
  ({ hasFetchedInitialItems }, currentItemFilter, like2BuyItems) =>
    hasFetchedInitialItems &&
    currentItemFilter === DEFAULT_TIKTOK_LIKE2BUY_FILTER &&
    like2BuyItems.length === 0,
);

export const getTikTokLike2BuyFilteredOrderedItems = createSelector(
  getTikTokLike2BuyOrderedItems,
  getTikTokLike2BuyCurrentItemFilter,
  (like2BuyItems, currentItemFilter) =>
    like2BuyItems.filter((item) => {
      const status = item.like2BuyV2ModerationStatus;

      if (currentItemFilter === Like2BuyV2ModerationItemFilter.AllMedia) {
        return item;
      }

      if (currentItemFilter === Like2BuyV2ModerationItemFilter.ActionNeeded) {
        return (
          status === Like2BuyV2ModerationStatus.NeedsProducts ||
          status === Like2BuyV2ModerationStatus.NeedsProductsAndRights ||
          status === Like2BuyV2ModerationStatus.NeedsRights
        );
      }

      if (currentItemFilter === Like2BuyV2ModerationItemFilter.Published) {
        return (
          status === Like2BuyV2ModerationStatus.Pinned ||
          status === Like2BuyV2ModerationStatus.Published
        );
      }

      return status === currentItemFilter;
    }),
);

export const getTikTokLike2BuyCanModerateStore = createSelector(
  getTikTokLike2BuyStoreData,
  hasTikTokLike2BuyModerationPermission,
  (like2BuyStore, hasTikTokLike2BuyModerationPermission) =>
    like2BuyStore && hasTikTokLike2BuyModerationPermission,
);

// if a Like2Buy store can't be found for the tiktokUser,
// either because there was an error fetching all Like2Buy stores for the client or there was no TikTok Like2Buy store for a valid user
// or there was an error fetching the user
export const getTikTokLike2BuyShouldShowErrorState = createSelector(
  getTikTokLike2BuyFlags,
  getTikTokLike2BuyErrors,
  getFetchTikTokUserError,
  ({ isFetchingStore }, { fetchStoreError }, fetchUserError) =>
    !isFetchingStore &&
    (isDefined(fetchUserError) || isDefined(fetchStoreError)),
);

const getSelectedItems = (state: ICuralateStore) =>
  state.bulkActions.selectedItems;

const getSelectedCuids = createSelector(
  getSelectedItems,
  getClientId,
  (items, clientId) =>
    items.map((item) => Cuid.fromPartial(item.cuid, clientId)),
);

export const getTikTokLike2BuySelectedItems = createSelector(
  getTikTokLike2BuyFilteredOrderedItems,
  getSelectedCuids,
  (items, selectedCuids) =>
    items.filter((item) => selectedCuids.includes(item.cuid)),
);

export const getTikTokLike2BuyHasMaxPinnedItems = createSelector(
  getTikTokLike2BuyPinnedItemCount,
  (count) => count >= MAXIMUM_PINNED_ITEMS_COUNT,
);

// using this for optimistic ui purposes - after an item's status changes we do not fetch all items
// (which is what is represented in getTotalCount) for the current filter so the total count displayed in the grid
// needs to be based on the initial getTotalCount and the number of items that should be displayed after status change
// (getTikTokLike2BuyFilteredOrderedItems)
export const getTikTokLike2BuyTotalItemCount = createSelector(
  getTotalCount,
  getTikTokLike2BuyFilteredOrderedItems,
  getTikTokLike2BuyOrderedItems,
  (totalItemCount, filteredItems, orderedItems) => {
    const unfetchedCount = totalItemCount - orderedItems.length;
    return unfetchedCount + filteredItems.length;
  },
);
